import { Archive, ArrowUpDown, LayoutDashboard, Plus, RotateCcw } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Avatar, AvatarStack } from '../components/Avatar'
import { Button } from '../components/Button'
import { DataTable, type DataTableColumn } from '../components/DataTable'
import { FilterBar, FilterSelect, SearchInput, SegmentedControl } from '../components/FilterBar'
import { PageHeader } from '../components/PageHeader'
import { StatusBadge } from '../components/StatusBadge'
import { useAppData } from '../context/AppDataContext'
import { useToast } from '../context/ToastContext'
import { useScopedWork } from '../hooks/useScopedWork'
import { apiErrorMessage } from '../lib/api'
import { formatDateLong } from '../lib/format'
import type { Client, TeamMember } from '../data/types'
import { AddClientModal, type ClientKind } from './clients/AddClientModal'

type SortKey = 'name' | 'activeProjects' | 'lastActivity'

/** The tabs: the three kinds of current client, then the ones the studio is done with. */
type Tab = ClientKind | 'previous' | 'archive'

// Prospects have a page of their own now (/prospects), where anybody can add
// one and the whole pipeline is visible; a tab here was a second place for
// the same list.
const TABS: Tab[] = ['recurring', 'one_off', 'previous', 'archive']

/**
 * Which tab a client who is not archived sits under.
 *
 * A prospect is somebody the studio may already be doing work for but who has
 * not signed up; they are on the Prospects page whichever way they would pay. A
 * former client, or a prospect who said no, is Previous — still on the list,
 * because a client who went quiet is the likeliest one to come back, until
 * somebody archives them. Recurring and One-off are only people working with
 * the studio now.
 */
function tabOf(c: Client): Tab {
  if (c.accountStatus === 'Prospect') return 'prospect'
  if (c.accountStatus === 'Former' || c.accountStatus === 'Lost') return 'previous'
  return c.engagement === 'One-off' ? 'one_off' : 'recurring'
}

const TAB_LABEL: Record<Tab, string> = {
  recurring: 'Recurring',
  one_off: 'One-off',
  prospect: 'Prospects',
  previous: 'Previous',
  archive: 'Archive',
}

const TAB_EMPTY: Record<Tab, string> = {
  recurring: 'Nobody on a monthly arrangement matches.',
  one_off: 'No one-off clients match. Add one for a single piece of work.',
  prospect: 'No prospects match. Add somebody you are pitching or already trialling work for.',
  previous: 'No previous clients. Former clients and prospects who said no land here.',
  archive: 'Nothing archived. Archive a previous client once the work is done; they can be brought back.',
}

const STATUS_FILTER: Partial<Record<Tab, { label: string; value: string }[]>> = {
  recurring: [
    { label: 'Active', value: 'Active' },
    { label: 'Unconfirmed', value: 'Unconfirmed' },
  ],
  one_off: [
    { label: 'Active', value: 'Active' },
    { label: 'Unconfirmed', value: 'Unconfirmed' },
  ],
  previous: [
    { label: 'Former', value: 'Former' },
    { label: 'Lost', value: 'Lost' },
  ],
}

export default function Clients() {
  const { team, directory, archivedClients, archiveClient, restoreClient } = useAppData()
  const { clients } = useScopedWork()
  const { showToast } = useToast()
  const navigate = useNavigate()
  const [tab, setTab] = useState<Tab>('recurring')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('all')
  const [industry, setIndustry] = useState('all')
  const [sortKey, setSortKey] = useState<SortKey>('name')
  const [addOpen, setAddOpen] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)

  const inTab = useMemo(
    () => (tab === 'archive' ? archivedClients : clients.filter((c) => tabOf(c) === tab)),
    [tab, clients, archivedClients],
  )

  const counts = useMemo(() => {
    const n: Record<Tab, number> = { recurring: 0, one_off: 0, prospect: 0, previous: 0, archive: archivedClients.length }
    for (const c of clients) n[tabOf(c)] += 1
    return n
  }, [clients, archivedClients])

  const industries = useMemo(() => Array.from(new Set(inTab.map((c) => c.industry))), [inTab])

  const filtered = useMemo(() => {
    let list = inTab.filter((c) => {
      if (search && !c.name.toLowerCase().includes(search.toLowerCase())) return false
      if (status !== 'all' && c.accountStatus !== status) return false
      if (industry !== 'all' && c.industry !== industry) return false
      return true
    })
    list = [...list].sort((a, b) => {
      if (sortKey === 'name') return a.name.localeCompare(b.name)
      if (sortKey === 'activeProjects') return b.activeProjects - a.activeProjects
      return a.lastActivity < b.lastActivity ? 1 : -1
    })
    return list
  }, [inTab, search, status, industry, sortKey])

  async function act(c: Client, how: 'archive' | 'restore') {
    if (busy !== null) return
    setBusy(c.id)
    try {
      if (how === 'archive') {
        await archiveClient(c.id)
        showToast(`${c.name} moved to the archive.`)
      } else {
        await restoreClient(c.id)
        showToast(
          c.accountStatus === 'Former'
            ? `${c.name} is back as an active client.`
            : `${c.name} is back on the list.`,
        )
      }
    } catch (err) {
      showToast(apiErrorMessage(err))
    } finally {
      setBusy(null)
    }
  }

  // From the directory, not the team list, so a partner leading a client is
  // on it too; in the order the assignments give, lead first.
  const assignedTeamFor = (c: Client) =>
    c.assignedTeamIds.map((id) => directory.find((m) => m.id === id)).filter((m): m is NonNullable<typeof m> => m !== undefined)

  const nameColumn: DataTableColumn<Client> = {
    key: 'client',
    header: 'Client',
    render: (c) => (
      <div className="flex items-center gap-3">
        <Avatar name={c.name} color={c.color} size="sm" />
        <span className="font-medium text-shell-text-active">{c.name}</span>
      </div>
    ),
  }
  const whatColumn: DataTableColumn<Client> = {
    key: 'industry',
    header: 'What they do',
    render: (c) => <span className="text-shell-text">{c.industry}</span>,
  }
  const actionColumn = (how: 'archive' | 'restore'): DataTableColumn<Client> => ({
    key: 'action',
    header: '',
    render: (c) => (
      <div className="flex justify-end">
        <Button
          variant="secondary"
          size="sm"
          disabled={busy !== null}
          onClick={(e) => {
            e.stopPropagation()
            void act(c, how)
          }}
        >
          {how === 'archive' ? <Archive size={14} /> : <RotateCcw size={14} />}
          {busy === c.id ? 'Working…' : how === 'archive' ? 'Archive' : 'Bring back'}
        </Button>
      </div>
    ),
  })

  const columns: DataTableColumn<Client>[] =
    tab === 'archive'
      ? [
          nameColumn,
          whatColumn,
          { key: 'status', header: 'Was', render: (c) => <StatusBadge status={c.accountStatus} /> },
          { key: 'engagement', header: 'How they paid', render: (c) => <span className="text-shell-text">{c.engagement}</span> },
          {
            key: 'archivedAt',
            header: 'Archived',
            render: (c) => (
              <span className="text-shell-text">{c.archivedAt ? formatDateLong(c.archivedAt.slice(0, 10)) : ''}</span>
            ),
          },
          actionColumn('restore'),
        ]
      : [
          nameColumn,
          whatColumn,
          {
            key: 'status',
            header: tab === 'prospect' ? 'Would be' : 'Status',
            render: (c) =>
              tab === 'prospect' ? (
                <span className="text-shell-text">{c.engagement}</span>
              ) : (
                <StatusBadge status={c.accountStatus} />
              ),
          },
          ...(tab === 'previous'
            ? [{ key: 'engagement', header: 'How they paid', render: (c: Client) => <span className="text-shell-text">{c.engagement}</span> }]
            : [
                { key: 'activeProjects', header: 'Cycle', render: (c: Client) => <span className="text-shell-text">{c.activeProjects}</span> },
                {
                  key: 'deliverables',
                  header: 'This month',
                  render: (c: Client) => (
                    <span className="text-shell-text">
                      {c.completedDeliverables} out &middot; {c.pendingDeliverables} owed
                    </span>
                  ),
                },
                {
                  key: 'team',
                  header: 'Who is on it',
                  render: (c: Client) => {
                    const assigned = assignedTeamFor(c)
                    return <AvatarStack names={assigned.map((m) => m.name)} colors={assigned.map((m) => m.color)} />
                  },
                },
              ]),
          {
            key: 'lastActivity',
            header: 'Last moved',
            // Already a phrase — "2 days ago" — not a date. It went through
            // `formatDate`, which is for ISO dates, so this column rendered
            // nonsense the moment it stopped being empty.
            render: (c) => <span className="text-shell-text">{c.lastActivity}</span>,
          },
          ...(tab === 'previous' ? [actionColumn('archive')] : []),
        ]

  const addKind: ClientKind = tab === 'one_off' || tab === 'prospect' ? tab : 'recurring'
  const statusOptions = STATUS_FILTER[tab]

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        variant="dark"
        title="Clients"
        description="Everyone the studio works for, and what each one is on."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <a
              href="/clients/canvas"
              target="_blank"
              rel="noopener"
              className="inline-flex items-center gap-1.5 rounded-lg border border-white/20 px-3 py-2 text-sm font-medium text-white hover:bg-white/10"
            >
              <LayoutDashboard size={16} /> Open canvas
            </a>
            <Button onClick={() => setAddOpen(true)}>
              <Plus size={16} /> {tab === 'prospect' ? 'Add a prospect' : 'Add a client'}
            </Button>
          </div>
        }
      />

      <div className="-mx-1 overflow-x-auto px-1">
        <SegmentedControl
          value={tab}
          onChange={(v) => {
            setTab(v as Tab)
            setStatus('all')
            setIndustry('all')
          }}
          options={TABS.map((t) => ({ label: `${TAB_LABEL[t]} · ${counts[t]}`, value: t }))}
        />
      </div>

      <FilterBar>
        <SearchInput value={search} onChange={setSearch} placeholder="Search clients…" className="w-full sm:w-64" />
        {statusOptions !== undefined && (
          <FilterSelect
            label="Filter by status"
            value={status}
            onChange={setStatus}
            options={[{ label: 'All statuses', value: 'all' }, ...statusOptions]}
          />
        )}
        <FilterSelect
          label="Filter by what they do"
          value={industry}
          onChange={setIndustry}
          options={[{ label: 'Anything', value: 'all' }, ...industries.map((i) => ({ label: i, value: i }))]}
        />
        <button
          onClick={() => setSortKey(sortKey === 'name' ? 'activeProjects' : sortKey === 'activeProjects' ? 'lastActivity' : 'name')}
          className="flex h-9 items-center gap-1.5 rounded-lg border border-shell-border bg-shell px-3 text-sm text-shell-text hover:bg-shell-border/40"
        >
          <ArrowUpDown size={13} />
          Sort: {sortKey === 'name' ? 'Name' : sortKey === 'activeProjects' ? 'Active projects' : 'Last activity'}
        </button>
      </FilterBar>

      <DataTable
        columns={columns}
        data={filtered}
        rowKey={(c) => c.id}
        // An archived client has no page until they are brought back.
        {...(tab === 'archive' ? {} : { onRowClick: (c: Client) => navigate(`/clients/${c.id}`) })}
        emptyTitle={`No ${TAB_LABEL[tab].toLowerCase()} clients here`}
        emptyDescription={TAB_EMPTY[tab]}
        minWidth={880}
      />

      <AddClientModal open={addOpen} kind={addKind} onClose={() => setAddOpen(false)} />
    </div>
  )
}
